import axios from 'axios';
import {useQueries, useQuery, useQueryClient} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import {User, Faction} from "../types/general.types.ts";
import {Sector} from "../pages/map/types.ts";
import {sortByName} from "./Sorting.ts";
import {useMemo} from "react";
import {
    CreatureType,
    DroidType,
    FacilityType,
    ItemType,
    MaterialType,
    ShipType,
    StationType,
    System,
    Terrain,
    UserLocation,
    VehicleType,
    WeaponType
} from "../types/swc.types.ts";

const api = axios.create({baseURL: import.meta.env.VITE_BACKEND_URL + 'api', withCredentials: true});

// Create a version update event
export const VERSION_UPDATE_EVENT = 'app-version-update';
const versionEventEmitter = new EventTarget();

api.interceptors.response.use((response) => {
    const serverVersion = response.headers['x-forge-version'];
    if (serverVersion) {
        versionEventEmitter.dispatchEvent(new CustomEvent(VERSION_UPDATE_EVENT, {
            detail: serverVersion
        }));
    }
    return response;
});

export { versionEventEmitter };

export default api;

export function on401Error(error: Error) {
    if (axios.isAxiosError(error)) {
        if (error.response && error.response.status === 401) {
            const client = useQueryClient();
            client.invalidateQueries({queryKey: ['user']});
        }
    }
}

interface LoggedUser {
    user: User,
    factions: Faction[],
    analyticsId?: string | null,
}

const emptyUser = {
    uid: '',
    handle: '',
    isAdmin: false,
    isEnabled: true,
    isLogged: false,
    factions: [],
    subLevel: 0,
    subExpire: null,
    subLicense: '',
    routeSkills: {
        hyper: 1,
        skill: 0,
    },
    privs: {
        manageTavernNetwork: false,
        manageLicenses: false,
        managePermissions: false,
    },
    moduleTypes: [],
} as User;

export function useUser() {
    const {data, ...info} = useQuery<LoggedUser>({
        queryKey: ['user'],
        queryFn: async () => {
            const {data} = await api.get('/users/myself');

            if(data.user) {
                data.user.privs = data.privs;
                data.user.moduleTypes = data.moduleTypes ?? [];
            }

            if(data.factions) {
                data.factions = data.factions.map((f: Faction) => {
                    if (data.privs.details[f.id]) {
                        return {...f, permissions: data.privs.details[f.id]}
                    }
                    return f;
                })
            }
            Sentry.setContext('user', data.user)
            return data;
        },
        staleTime: 1000 * 60 * 5,
        retry: 2,
        refetchOnWindowFocus: false,
    });
    return {
        user: data ? data.user as User : emptyUser,
        factions: data ? data.factions as Faction[] : [],
        analyticsId: data?.analyticsId ?? null,
        ...info,
    }
}

export function useSWCLocation() {
    const {data, ...info} = useQuery({
        queryKey: ['user-location'],
        queryFn: async () => {
            const {data} = await api.get('/user/location');
            return data as UserLocation;
        },
        staleTime: 1000 * 60 * 5,
    });

    return {
        location: data ? data : null,
        ...info
    }
}

type AvailableEntityType = 'ships' | 'vehicles' | 'stations' | 'facilities' | 'materials' | 'weapons' | 'droids' | 'items' | 'creatures' | 'terrains';

type EntityData = {
    ships: ShipType[];
    vehicles: VehicleType[];
    stations: StationType[];
    facilities: FacilityType[];
    materials: MaterialType[];
    weapons: WeaponType[];
    items: ItemType[];
    droids: DroidType[];
    creatures: CreatureType[];
    terrains: Terrain[];
};
type UseEntityTypeReturn<T extends AvailableEntityType[]> = {
    [K in T[number]]: EntityData[K];
} & {
    isPending: boolean;
    isError: boolean;
};

const sortEntities = (a: any, b: any) => a.name.localeCompare(b.name);

export function useEntityTypes<T extends AvailableEntityType[]>(types: T, sorted: boolean = false): UseEntityTypeReturn<T> {
    const queries = useQueries({
            "queries": types.map((type) => ({
                queryKey: ['entityType', type],
                queryFn: async () => {
                    const response = await api.get(`/data/entities/${type}`);
                    return response.data;
                },
                staleTime: 1000 * 60 * 5,
            })),
        }
    );

    const data = {} as UseEntityTypeReturn<T>;
    types.forEach((type, index) => {
        // @ts-ignore
        data[type] = (queries[index].data ?? []) as EntityData[typeof type];
        if (sorted) {
            // @ts-ignore
            data[type] = data[type].sort(sortEntities);
        }
    });

    const isPending = queries.some(q => q.isPending);
    const isError = queries.some(q => q.isError);

    return {...data, isPending, isError};
}

export function usePreviousEntityTypes<T extends AvailableEntityType[]>(types: T, sorted: boolean = false): UseEntityTypeReturn<T> {
    const queries = useQueries({
            "queries": types.map((type) => ({
                queryKey: ['entityTypeOld', type],
                queryFn: async () => {
                    const response = await api.get(`/data/entities-old/${type}`);
                    return response.data;
                },
                staleTime: 1000 * 60 * 5,
            })),
        }
    );

    const data = {} as UseEntityTypeReturn<T>;
    types.forEach((type, index) => {
        // @ts-ignore
        data[type] = (queries[index].data ?? []) as EntityData[typeof type];
        if (sorted) {
            // @ts-ignore
            data[type] = data[type].sort(sortEntities);
        }
    });

    const isPending = queries.some(q => q.isPending);
    const isError = queries.some(q => q.isError);

    return {...data, isPending, isError};
}

export function useEntityHistory(type: AvailableEntityType, uid?: string) {
    return useQuery({
        queryKey: ['entityHistory', type, uid],
        queryFn: async () => {
            const params = uid ? `?uid=${uid}` : '';
            const response = await api.get(`/data/entities-history/${type}${params}`);
            return response.data;
        },
        staleTime: 1000 * 60 * 5,
    });
}

async function getSharedSystems(){
    const { data } = await api.get('/data/systems');
    const filteredSystems = data.toSorted(sortByName)
    return filteredSystems as System[];
}

export function useSystems() {
    let {data: systems, ...others} = useQuery({
        queryKey: ['systems'],
        queryFn: getSharedSystems,
        staleTime: Infinity,
    })
    if(others.isLoading || typeof systems === 'undefined') {
        systems = []
    }
    return {systems: systems, ...others};
}

export function useBHSystems() {
  const {systems, ...others} = useSystems();

  const filtered = useMemo(() => {
    return systems.filter(s => s.flagBH);
  }, [systems]);

  return {systems: filtered, ...others};

}

async function getSharedSectors(){
    const { data } = await api.get('/data/sectors');
    return data as Sector[];
}

export function useSectors() {
    let {data: sectors, ...others} = useQuery({
        queryKey: ['sectors'],
        queryFn: getSharedSectors,
        staleTime: Infinity,
    })
    if(others.isLoading || typeof sectors === 'undefined') {
        sectors = []
    }

    return {sectors, ...others};
}


async function getSharedFactions() {
  const {data} = await api.get('/data/factions');
  return data as Faction[];
}

export function useFactions() {
  let {data: factions, ...others} = useQuery({
    queryKey: ['factions'],
    queryFn: getSharedFactions,
  })

  if(others.isLoading || typeof factions === 'undefined') {
    factions = [];
  }
  return {factions, ...others}
}
